import React from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {EndlessList, mapListConfig} from 'components/ui-kit/EndlessList2.0';
import {
    filtersChanged,
    filtersReset,
    filtersSubmit,
    Gate,
    listState$,
    sortingsChanged,
} from 'models/products/grid/list';
import {
    ProductsGridAccessors,
    ProductsGridFormattedRow,
    ProductsGridRawRow,
} from 'types/products-grid';

import {ListRow, FlexRowElement, RowElement, Text} from './styled';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: ProductsGridRawRow[] | undefined,
        t: TFunction,
    ) => ProductsGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const ProductsGridList = ({configFunc, dataMapper, exportConfig}: Props) => {
    const {t} = useTranslation();
    useGate(Gate);
    const {data, filters, sortings, pageIncremented, showConfigBlock} = useStore(listState$);
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);

    return (
        <EndlessList<ProductsGridFormattedRow>
            rows={mappedData}
            hasMore={data?.hasNext}
            renderRow={(row: ProductsGridFormattedRow) => (
                <ListRow>
                    <FlexRowElement>
                        <Text>{row[ProductsGridAccessors.modifiedAt]}</Text>
                        <Text isGray>{row[ProductsGridAccessors.status]}</Text>
                    </FlexRowElement>
                    <RowElement>
                        <Text>{row[ProductsGridAccessors.code]}</Text>
                    </RowElement>
                    <RowElement>
                        <Text>{row[ProductsGridAccessors.name]}</Text>
                    </RowElement>
                    <RowElement>
                        <Text isGray>
                            {row[ProductsGridAccessors.packageType]}
                            {' • '}
                            {row[ProductsGridAccessors.manufacturer]}
                        </Text>
                    </RowElement>
                </ListRow>
            )}
            onNextPage={() => pageIncremented()}
            exportConfig={exportConfig}
            configurationBlock={mapListConfig(
                configFunc(t),
                filters,
                sortings,
                (value) => filtersChanged([value]),
                filtersSubmit,
                filtersReset,
                sortingsChanged,
                showConfigBlock,
            )}
        />
    );
};
